<script setup lang="ts">
definePageMeta({ layout: 'marketing' })
</script>

<template>
  <MarketingVerticalLandingPage
    vertical="real-estate-agencies"
    accent_from="from-emerald-600"
    accent_to="to-teal-500"
    :hero="{
      eyebrow: 'For independent sales + leasing agencies',
      headline: 'Every sale and lease, from appraisal to settlement — out of the spreadsheet.',
      subhead: 'Track each transaction through its stages, keep cooling-off, finance and settlement dates on the calendar, hold deposits on a proper trust ledger, and give vendors, buyers and tenants a portal instead of a phone call.',
      cta_text: 'See the sale workflow'
    }"
    :features="[
      { icon: '🏠', title: 'Sale + lease workflows', description: 'Residential sale (appraisal → listing → marketing → offer → exchanged → unconditional → settled) and residential lease (application → checks → signed → bond → moved in) ship ready to use.' },
      { icon: '📅', title: 'Key dates you can\'t miss', description: 'Cooling-off ends, finance clause due, settlement date, bond lodgement — created automatically when a transaction moves stage, with reminders at 14, 7, 3, 1 days.' },
      { icon: '🏛️', title: 'Deposits on trust', description: 'Buyer deposits and holding deposits recorded on an append-only trust ledger with per-client balances and statements.' },
      { icon: '✍️', title: 'E-signed agency agreements', description: 'Send the agency agreement or lease for electronic signature from the transaction. The signed copy lands back on the file.' },
      { icon: '🤝', title: 'Client portal', description: 'Vendors watch enquiries and offers, buyers see their key dates, tenants upload their 100 points — all through a secure link.' },
      { icon: '💳', title: 'Fees, invoices, Xero', description: 'Commission and letting-fee invoices with card or bank-transfer payment, synced to Xero as drafts.' }
    ]"
    :workflow_steps="[
      { step: '1', title: 'Enquiry → appraisal', description: 'Enquiry arrives via your website form → follow-up task is created → appraisal booked → agency agreement e-signed.' },
      { step: '2', title: 'Market → exchange', description: 'Listing goes live, offers logged, contract exchanged — cooling-off, deposit and finance dates land on the calendar automatically.' },
      { step: '3', title: 'Unconditional → settle', description: 'Finance confirmed, settlement booked, deposit drawn from trust, invoice paid, review requested.' }
    ]"
    :pain_points="[
      'Cooling-off and finance dates living in one agent\'s head — or a paper diary',
      'Deposits tracked in a spreadsheet nobody reconciles',
      'Vendors calling for updates because there is nowhere to look',
      'The same contract emailed back and forth six times'
    ]"
    :relief_points="[
      'Every key date created automatically and synced to calendars',
      'Append-only trust ledger with per-client balances and statements',
      'Vendors, buyers and tenants see stages, documents and dates in their portal',
      'Contracts e-signed from the transaction and filed automatically'
    ]"
    :sample_screen="{
      title: '14 Wattle St, Marrickville',
      subtitle: 'Transaction #2026-031 · Residential sale',
      items: [
        { label: 'Stage', value: 'Exchanged' },
        { label: 'Agreed price', value: '$1,420,000' },
        { label: 'Cooling-off ends', value: '12 Sep 2026' },
        { label: 'Finance due', value: '21 Sep 2026' },
        { label: 'Deposit on trust', value: '$142,000 held' }
      ]
    }"
    :faq="[
      { q: 'Does it do property management?', a: 'No, and we don\'t pretend to. Rent cycles, routine inspections and maintenance need a dedicated property-management system. We cover sales and lease sign-up: the transaction from first enquiry to settlement or move-in.' },
      { q: 'Is the trust ledger a compliant trust account?', a: 'It is the bookkeeping layer: an append-only ledger with full audit trail, per-client balances and exportable statements. Your statutory trust account and audits stay with your bank and accountant.' },
      { q: 'Can I customise the stages and key dates?', a: 'Yes. The sale and lease workflows are templates — edit stages, documents, tasks and key-date offsets, or add your own workflow for commercial or off-the-plan sales.' },
      { q: 'Does it replace my listing portal?', a: 'No. You keep publishing to the portals you use; this is where the transaction itself is managed once an enquiry becomes a client.' }
    ]"
    pricing_callout="Sized for independent agencies and small franchise offices."
  />
</template>
